import React, { useState, useEffect } from "react";
import { fetchProfile, getCachedProfile } from "../api";
import NotifyButton from "./NotifyButton";
import { getVenueInfo } from "../utils/venueInfo";
import { getSchedule } from "../utils/rosteredNames";

// One row of the availability block. Kept verbose on purpose: "not on tomorrow's
// roster" and "tomorrow isn't published yet" mean completely different things to
// someone deciding whether to wait.
const ScheduleRow = ({ label, day }) => {
  if (!day) return null;
  const { status, time } = day;
  const className =
    status === "working"
      ? "profile-schedule__row is-working"
      : "profile-schedule__row";

  let value;
  if (status === "working") {
    value = <strong>{time || "time not listed"}</strong>;
  } else if (status === "unpublished") {
    value = <span className="profile-schedule__muted">not published yet</span>;
  } else {
    value = <span className="profile-schedule__muted">not on this roster</span>;
  }

  return (
    <div className={className}>
      <span className="profile-schedule__day">{label}</span>
      <span className="profile-schedule__value">{value}</span>
    </div>
  );
};

// Section headers that appear in scraped bios, glued to the surrounding text with no
// whitespace (e.g. "each day!MeasurementsCup: B"). Each one gets pushed onto its own line.
const SECTION_HEADERS = ["Measurements", "Services Provided", "Appearance", "Special Pricing"];

// Short "Label: value" fields that show up inside bios and stat blocks. Each one gets
// pushed onto its own line so it can be rendered as a spec row instead of running prose.
const SUB_LABELS = [
  "Name", "Age", "Body Size", "Cup Size", "Height", "Weight", "Dress Size", "Language",
  "Type", "Speciality", "Booking", "Cup", "Skin Complexion", "Eyes", "Hair", "Photos",
];

// Scraped venue pages often drop the whitespace between elements, gluing words and
// numbers together ("UlanJapanese", "42kgSpecial"). Re-insert spaces at those boundaries
// and give known section/label headers their own line so the text reads like prose again.
const declumpText = (raw) => {
  let text = raw;
  text = text.replace(/([a-z])([A-Z])/g, "$1 $2");
  text = text.replace(/([a-zA-Z])(\d)/g, "$1 $2");
  text = text.replace(/(\d)([A-Za-z])/g, "$1 $2");

  // No5/Nightshade pricing is "(Including room hire)30 / 45 / 60200 / 250 / 300" - the
  // fixed duration list (30/45/60 mins) runs straight into the price list with no
  // separator, so split them onto their own lines before the value gets treated as prose.
  text = text.replace(
    /\(\s*including room hire\s*\)\s*30\s*\/\s*45\s*\/\s*60\s*(\d[\d\s/]*)/gi,
    (_match, prices) => `(Including room hire)\n30/45/60\n${prices.replace(/\s+/g, "")}`
  );

  text = text.replace(/\s*\/\s*/g, "/");

  // No leading \b here: single-letter values are often glued straight onto the next
  // label with no separator at all (e.g. "Cup: BHeight: 150cm", "in YAppearance").
  const headerPattern = new RegExp(`\\s*(${SECTION_HEADERS.join("|")})\\s*`, "g");
  text = text.replace(headerPattern, "\n\n$1\n");

  const labelPattern = new RegExp(`\\s*(${SUB_LABELS.join("|")})\\s*:\\s*`, "g");
  text = text.replace(labelPattern, "\n$1: ");

  // Break onto a new line after a sentence ends and the next one starts with no space.
  text = text.replace(/([.!?])\s*(?=[A-Z])/g, "$1\n");

  return text
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .filter(Boolean);
};

// Renders a block of scraped stats/bio text as a mix of section headings, compact
// "label: value" rows, and prose paragraphs - instead of one unreadable wall of text.
const renderTextBlock = (raw, keyPrefix) => {
  const lines = declumpText(raw);
  const rowPattern = new RegExp(`^(${SUB_LABELS.join("|")})\\s*:\\s*(.+)$`);

  return (
    <>
      {lines.map((line, i) => {
        const key = `${keyPrefix}-${i}`;
        if (SECTION_HEADERS.includes(line)) {
          return <h6 key={key} className="profile-section-heading">{line}</h6>;
        }
        const rowMatch = line.match(rowPattern);
        if (rowMatch) {
          return (
            <dl className="profile-spec-row" key={key}>
              <dt>{rowMatch[1]}</dt>
              <dd>{rowMatch[2]}</dd>
            </dl>
          );
        }
        return <p className="profile-bio-line" key={key}>{line}</p>;
      })}
    </>
  );
};

// Pulls the enumerable "Can do: ..." and "extra: ..." lists out of a Ginza-style bio so
// they render as scannable tag pills instead of running into the prose paragraph above them.
const splitBioSections = (raw) => {
  let body = raw.trim();
  let extraItems = [];
  let canDoItems = [];

  const extraMatch = body.match(/^(.*?)(?:☆\s*)?extra\s*:\s*(.+)$/is);
  if (extraMatch) {
    body = extraMatch[1].trim();
    extraItems = extraMatch[2].split(",").map((s) => s.trim().replace(/\.$/, "")).filter(Boolean);
  }

  const canDoMatch = body.match(/^(.*?)Can do\s*:\s*(.+)$/is);
  if (canDoMatch) {
    body = canDoMatch[1].trim();
    canDoItems = canDoMatch[2].split(",").map((s) => s.trim().replace(/\.$/, "")).filter(Boolean);
  }

  return { body, canDoItems, extraItems };
};

const renderTagSection = (label, items, modifier) => {
  if (items.length === 0) return null;
  return (
    <div className="profile-tag-section">
      <span className="profile-tag-label">{label}</span>
      <div className="profile-tags">
        {items.map((item, i) => (
          <span className={`profile-tag${modifier ? ` ${modifier}` : ""}`} key={i}>{item}</span>
        ))}
      </div>
    </div>
  );
};

// Details & pricing block: renders newline-separated "label: value" stats cleanly, or
// declumps a concatenated blob (No5/Nightshade style) into the same row format.
const renderStats = (stats) => {
  if (!stats) return null;
  if (Array.isArray(stats)) {
    return stats.map((p, i) => <p className="profile-bio-line" key={i}>{p}</p>);
  }
  return renderTextBlock(stats, "stats");
};

// About block: separates the "Can do"/"extra" lists from the prose, then declumps and
// paragraphs whatever's left.
const renderDescription = (description) => {
  if (!description) return null;
  const { body, canDoItems, extraItems } = splitBioSections(description);
  return (
    <>
      {body && renderTextBlock(body, "bio")}
      {renderTagSection("Can do", canDoItems)}
      {renderTagSection("Extras", extraItems, "profile-tag--extra")}
    </>
  );
};

const ProfileModal = ({
  show,
  onHide,
  profileName,
  venue = "marrickville",
  onNavigate,
  rosterData,
}) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && profileName) {
      const cachedProfile = getCachedProfile(profileName, venue);
      if (cachedProfile) {
        setProfileData(cachedProfile);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      // Fetch profile details from backend
      fetchProfile(profileName, venue)
        .then((data) => {
          setProfileData(data);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    } else {
      setProfileData(null);
    }
  }, [show, profileName, venue]);

  const [expandedIndex, setExpandedIndex] = useState(null);

  // Full list of resolved image URLs for this profile, used by both the grid and the
  // carousel overlay so the overlay can navigate through every photo, not just one.
  const resolvedImages = profileData
    ? profileData.image_url
      ? [profileData.image_url.startsWith("http") ? profileData.image_url : `/api${profileData.image_url}`]
      : profileData.images
      ? profileData.images.map((url) => (url.startsWith("http") ? url : `/api${url}`))
      : []
    : [];

  // Shopfront contact details for the venue this profile belongs to, used by the
  // call/message actions. Null for an unknown venue slug, in which case those two
  // buttons are simply omitted rather than rendering dead links.
  const venueContact = getVenueInfo(venue);

  // Shifts come from the roster already loaded by App, not from the profile API
  // (which only stores stats/description/images and has no notion of a schedule).
  const schedule =
    rosterData && profileName ? getSchedule(rosterData, venue, profileName) : null;

  // Two ways a profile can have nothing to show: the API answered "not found" (which
  // it does with HTTP 200 and an {error} body), or a record exists but was captured
  // with only a name and no details. Both should read the same to the user.
  const hasNothingToShow =
    profileData &&
    (Boolean(profileData.error) ||
      (!profileData.stats && !profileData.description && !profileData.image_url &&
        !(profileData.images && profileData.images.length > 0)));

  const showPrevImage = () => {
    setExpandedIndex((i) => (i - 1 + resolvedImages.length) % resolvedImages.length);
  };
  const showNextImage = () => {
    setExpandedIndex((i) => (i + 1) % resolvedImages.length);
  };

  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (event) => {
      if (expandedIndex !== null) {
        if (event.key === "Escape") {
          setExpandedIndex(null);
        } else if (event.key === "ArrowLeft") {
          showPrevImage();
        } else if (event.key === "ArrowRight") {
          showNextImage();
        }
        return;
      }
      if (event.key === "Escape") {
        onHide();
      } else if (event.key === "ArrowLeft" && onNavigate) {
        onNavigate(-1);
      } else if (event.key === "ArrowRight" && onNavigate) {
        onNavigate(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [show, onHide, onNavigate, expandedIndex, resolvedImages.length]);

  // Handle outside click
  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  };

  if (!show) return null;

  // Render the expanded image carousel overlay if an image is selected
  if (expandedIndex !== null && resolvedImages.length > 0) {
    const hasMultiple = resolvedImages.length > 1;
    return (
      <div 
        onClick={() => setExpandedIndex(null)}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
          cursor: 'zoom-out'
        }}
      >
        <button 
          onClick={() => setExpandedIndex(null)}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          &times;
        </button>
        {hasMultiple && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); showPrevImage(); }}
              aria-label="Previous photo"
              style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                color: 'white',
                fontSize: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001
              }}
            >
              &#8249;
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); showNextImage(); }}
              aria-label="Next photo"
              style={{
                position: 'absolute',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                color: 'white',
                fontSize: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001
              }}
            >
              &#8250;
            </button>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                background: 'rgba(0,0,0,0.5)',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '14px',
                zIndex: 10001
              }}
            >
              {expandedIndex + 1} / {resolvedImages.length}
            </div>
          </>
        )}
        <img 
          src={resolvedImages[expandedIndex]} 
          alt="Expanded Profile" 
          onClick={(e) => e.stopPropagation()}
          style={{ 
            maxWidth: '100%', 
            maxHeight: '100%', 
            objectFit: 'contain',
            borderRadius: '4px',
            cursor: 'default'
          }} 
        />
      </div>
    );
  }

  return (
    <div 
      onClick={handleOutsideClick}
      style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      {onNavigate && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
          aria-label="Previous profile"
          style={{
            position: 'absolute',
            left: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '44px',
            height: '44px',
            color: 'white',
            fontSize: '26px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          &#8249;
        </button>
      )}
      {onNavigate && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
          aria-label="Next profile"
          style={{
            position: 'absolute',
            right: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '44px',
            height: '44px',
            color: 'white',
            fontSize: '26px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          &#8250;
        </button>
      )}
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'var(--shadow-lg)',
        overflow: 'hidden',
        border: '1px solid var(--border-color)',
      }}>
        {/* Header */}
        <div style={{ padding: '15px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', backgroundColor: 'var(--bg-secondary)' }}>
          <h4 style={{ margin: 0, color: 'var(--text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{profileName} Profile</h4>
          {/* The notify control lives in the action row in the body now, alongside
              call/message, rather than competing with the close button up here. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            <button onClick={onHide} aria-label="Close profile" style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', lineHeight: '1', color: 'var(--text-primary)' }}>&times;</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div className="text-center py-4" style={{ color: 'var(--text-primary)' }}>Loading profile details...</div>
          ) : error ? (
            <div className="alert alert-warning">{error}</div>
          ) : hasNothingToShow ? (
            <div className="alert alert-info" role="status">
              <strong>No saved profile details for {profileName} yet.</strong>
              <div className="small mt-1">
                Details and photos are captured when someone appears on a scraped roster.
                They'll show up here after {profileName} next appears.
              </div>
            </div>
          ) : profileData ? (
            <div>
              {/* Availability first - it's the most time-sensitive thing here and
                  the reason most people open a profile. */}
              {schedule && (
                <div className="profile-schedule">
                  <h5 className="profile-schedule__title">Availability</h5>
                  <ScheduleRow label="Today" day={schedule.today} />
                  <ScheduleRow label="Tomorrow" day={schedule.tomorrow} />
                </div>
              )}

              {/* Stats/Prices Array */}
              <div style={{ marginBottom: '20px', backgroundColor: 'var(--bg-tertiary)', padding: '15px', borderRadius: '6px' }}>
                <h5 style={{ borderBottom: '2px solid var(--border-color)', paddingBottom: '5px', marginBottom: '10px', color: 'var(--text-primary)' }}>Details & Pricing</h5>
                {profileData.stats ? (
                  renderStats(profileData.stats)
                ) : (
                  <p className="text-muted" style={{ color: 'var(--text-muted)' }}>No pricing or details found.</p>
                )}
              </div>

              {/* Actions. Sits above About (and so above the photos) deliberately:
                  placed immediately before the photos it measured at 860-1109px on an
                  844px-tall phone, i.e. below the fold for every venue. Here it lands
                  in view as soon as the dialog opens. tel:/sms: hand off to the
                  device's phone and messaging apps. */}
              <div className="profile-actions">
                {venueContact && (
                  <>
                    <a
                      className="profile-action profile-action--call"
                      href={`tel:${venueContact.phoneDial}`}
                    >
                      <span aria-hidden="true">📞</span> Call venue
                    </a>
                    {/* No ?body= prefill: the syntax differs between iOS and Android
                        and a wrong one produces a broken link. */}
                    <a
                      className="profile-action profile-action--sms"
                      href={`sms:${venueContact.phoneDial}`}
                    >
                      <span aria-hidden="true">💬</span> Message
                    </a>
                  </>
                )}
                {profileName ? (
                  <NotifyButton venue={venue} performerName={profileName} showLabel />
                ) : null}
              </div>
              {venueContact && (
                <p className="profile-actions__hint">
                  Calls and messages go to <strong>{venueContact.name}</strong>{" "}
                  ({venueContact.phone}) — the venue, not {profileName} directly.
                </p>
              )}

              {/* Description */}
              <div style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                <h5>About</h5>
                {profileData.description ? (
                  renderDescription(profileData.description)
                ) : (
                  <p className="text-muted" style={{ color: 'var(--text-muted)' }}>No description available.</p>
                )}
              </div>
              
              {/* Images */}
              {profileData.image_url ? (
                <div>
                  <h5>Photos</h5>
                  <div style={{ borderRadius: '6px', overflow: 'hidden' }}>
                    <img 
                      src={resolvedImages[0]} 
                      alt={`${profileName}`} 
                      style={{ width: '100%', height: 'auto', display: 'block', maxWidth: '400px', cursor: 'zoom-in' }} 
                      loading="lazy" 
                      onClick={() => setExpandedIndex(0)}
                    />
                  </div>
                </div>
              ) : profileData.images && profileData.images.length > 0 ? (
                <div>
                  <h5>Photos</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px' }}>
                    {resolvedImages.map((finalUrl, i) => (
                      <div key={i} style={{ borderRadius: '6px', overflow: 'hidden' }}>
                         <img 
                            src={finalUrl} 
                            alt={`${profileName} ${i+1}`} 
                            style={{ width: '100%', height: 'auto', display: 'block', cursor: 'zoom-in' }} 
                            loading="lazy" 
                            onClick={() => setExpandedIndex(i)}
                          />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;